import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Drawer, DrawerContent } from '@atlaskit/drawer/compiled';
import { token } from '@atlaskit/tokens';

import { AdminhubIntlProvider } from '../../common/utils/locale';

import { Content } from './content';
import { Footer } from './footer';
import { Header } from './header';
import { ContentWrapper, DrawerContainer, DrawerWrapper, ParentContentWrapper } from './styled';
import type { FocusedTaskProps, Step } from './types';

const styles = cssMap({
	drawerContent: {
		marginTop: token('space.0'),
	},
});

const FocusedTaskImpl = ({
	testId = 'focused-task',
	currentStep,
	isOpen,
	steps,
	onClose,
	onCloseComplete,
	onOpenComplete,
	headerRightContent,
	rightContent,
	zIndex,
	ariaLabel,
	titleId,
}: FocusedTaskProps) => {
	const currentStepConfig: Step = steps[currentStep];
	const {
		content,
		isSubmissionLoading,
		isFetchLoading,
		errorContent,
		hasError,
		formId,
		backButtonOnClick,
		nextButtonOnClick,
		type,
	} = currentStepConfig;

	const handleDrawerOnClose = (): void => {
		onClose && onClose();
	};

	const shouldShowHeader: boolean = type === 'progress';
	const shouldShowFooter: boolean =
		!hasError && !isFetchLoading && (!!backButtonOnClick || !!nextButtonOnClick || !!formId);
	const contentSize = currentStepConfig.contentSize || 'medium';

	return (
		<Drawer
			testId={testId}
			zIndex={zIndex}
			width="full"
			isOpen={isOpen}
			onClose={() => handleDrawerOnClose()}
			onOpenComplete={onOpenComplete}
			onCloseComplete={onCloseComplete}
			label={ariaLabel}
			titleId={titleId}
		>
			<DrawerContent xcss={styles.drawerContent}>
				<DrawerContainer>
					<DrawerWrapper>
						<Header
							testId={testId}
							steps={steps}
							currentStep={currentStep}
							shouldShowHeader={shouldShowHeader}
							handleDrawerOnClose={handleDrawerOnClose}
							headerRightContent={headerRightContent}
						/>
						<ParentContentWrapper>
							<ContentWrapper data-testid={`${testId}-content`} contentSize={contentSize}>
								<Content
									isFetchLoading={isFetchLoading}
									hasError={hasError}
									isSubmissionLoading={isSubmissionLoading}
									content={content}
									errorContent={errorContent}
									testId={testId}
								/>
							</ContentWrapper>
						</ParentContentWrapper>
						{shouldShowFooter && (
							<Footer
								contentSize={contentSize}
								currentStepConfig={currentStepConfig}
								testId={testId}
								currentStep={currentStep}
							/>
						)}
					</DrawerWrapper>
					{rightContent}
				</DrawerContainer>
			</DrawerContent>
		</Drawer>
	);
};

export const FocusedTask = (props: FocusedTaskProps) => (
	<AdminhubIntlProvider>
		<FocusedTaskImpl {...props} />
	</AdminhubIntlProvider>
);

export default FocusedTask;
